"use client";

import { useCallback, useEffect, useState } from "react";
import { LEADERBOARD_REFRESH_MS } from "../_logic/constants";
import type { LeaderboardEntry } from "../_logic/types";

/**
 * Today's leaderboard, refreshed on an interval and on demand.
 * @param refreshSignal Change this value to force an immediate refetch (e.g. when a round finishes).
 * @returns `{ entries, loading, error }`.
 */
export function useLeaderboard(refreshSignal: unknown) {
  // States
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Handlers
  const load = useCallback(async () => {
    try {
      const res = await fetch("/api/wordle/leaderboard", { cache: "no-store" });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      setEntries(data.entries);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Effects
  useEffect(() => {
    load();
  }, [load, refreshSignal]);

  useEffect(() => {
    const id = setInterval(load, LEADERBOARD_REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  return { entries, loading, error };
}
